import React, { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import cn from "classnames";
import { disableBodyScroll, clearAllBodyScrollLocks } from "body-scroll-lock";
import styles from "./ModalTutor.module.sass";
import Icon from "../Icon";
import Card from "../Card";
import TextInput from "../TextInput";
import PhoneInput from "../PhoneInput";
import Switch from "../Switch";
import Dropdown from "../Dropdown";

const ModalTutor = ({ visible, onClose, onSave, tutor }) => {
    const [formData, setFormData] = useState({
        last_name: '',
        first_name: '',
        birth_date: '',
        email: '',
        phone: '',
        job_title: '',
        highest_degree_title: '',
        highest_degree_level: '',
        verified: false,
        is_apt_tutor: false,
    });

    const DEGREE_LEVEL_OPTIONS = [
        { value: '3', title: '3 : CAP, BEP' },
        { value: '4', title: '4 : Baccalauréat' },
        { value: '5', title: '5 : DEUG, BTS, DUT, DEUST' },
        { value: '6', title: '6 : Licence, licence professionnelle, BUT, Maîtrise' },
        { value: '7', title: '7 : Master, diplôme d\'études approfondies, diplôme d\'études supérieures spécialisées, diplôme d\'ingénieur' },
        { value: '8', title: '8 : Doctorat, habilitation à diriger des recherches' },
        { value: '0', title: '0 : Aucun' },
    ];

    useEffect(() => {
        if (visible) {
            const target = document.querySelector("#modal-tutor");
            if (target) {
                disableBodyScroll(target);
            }
            if (tutor) {
                const birthDate = tutor.birth_date ? new Date(tutor.birth_date).toISOString().split('T')[0] : '';
                setFormData({
                    last_name: tutor.last_name || '',
                    first_name: tutor.first_name || '',
                    birth_date: birthDate,
                    email: tutor.email || '',
                    phone: tutor.phone || '',
                    job_title: tutor.job_title || '',
                    highest_degree_title: tutor.highest_degree_title || '',
                    highest_degree_level: tutor.highest_degree_level || '',
                    verified: tutor.verified || false,
                    is_apt_tutor: tutor.is_apt_tutor || false,
                });
            } else {
                // Reset form for new tutor
                setFormData({
                    last_name: '',
                    first_name: '',
                    birth_date: '',
                    email: '',
                    phone: '',
                    job_title: '',
                    highest_degree_title: '',
                    highest_degree_level: '',
                    verified: false,
                    is_apt_tutor: false,
                });
            }
        } else {
            clearAllBodyScrollLocks();
        }
    }, [visible, tutor]);

    const escFunction = useCallback((e) => {
        if (e.keyCode === 27) {
            onClose();
        }
    }, [onClose]);

    useEffect(() => {
        document.addEventListener("keydown", escFunction, false);
        return () => {
            document.removeEventListener("keydown", escFunction, false);
        };
    }, [escFunction]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value,
        }));
    };

    const handleSwitchChange = (name, value) => {
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        const dataToSave = {
            ...formData,
            birth_date: formData.birth_date ? new Date(formData.birth_date).toISOString() : null,
        };
        onSave(dataToSave);
    };

    return createPortal(
        visible && (
            <div id="modal-tutor" className={styles.modal}>
                <div className={styles.control}>
                    <button className={styles.close} onClick={onClose}>
                        <Icon name="close" size="20" />
                    </button>
                </div>
                <div className={styles.outer}>
                    <Card
                        className={styles.card} // We will add width to styles.card in SASS
                        title={tutor ? 'Modifier le tuteur' : 'Ajouter un tuteur'}
                        classTitle="title-blue"
                    >
                        <div className={styles.description}>
                            <div className={styles.field} style={{ position: 'relative', zIndex: 9999, marginBottom: '40px' }}>
                                <form style={{ marginBottom: '20px', fontSize: '16px' }} onSubmit={handleSubmit}>

                                    <div className={styles.group}>
                                        <TextInput
                                            className={styles.field}
                                            label="Nom"
                                            name="last_name"
                                            value={formData.last_name}
                                            onChange={handleChange}
                                            required
                                        />
                                        <TextInput
                                            className={styles.field}
                                            label="Prénom"
                                            name="first_name"
                                            value={formData.first_name}
                                            onChange={handleChange}
                                            required
                                        />
                                        <TextInput
                                            className={styles.field}
                                            label="Date de naissance"
                                            name="birth_date"
                                            type="date"
                                            value={formData.birth_date}
                                            onChange={handleChange}
                                        />
                                    </div>

                                    <div className={styles.divider} />

                                    <div className={styles.group}>
                                        <TextInput
                                            className={styles.field}
                                            label="Email"
                                            name="email"
                                            type="email"
                                            value={formData.email}
                                            onChange={handleChange}
                                            required
                                        />
                                        <PhoneInput
                                            className={styles.field}
                                            label="Numéro de téléphone"
                                            name="phone"
                                            id="phone"
                                            placeholder="Numéro de téléphone"
                                            value={formData.phone || ''}
                                            onChange={(phone) => {
                                                console.log('Phone value in modal:', phone.target.value);
                                                setFormData(prev => ({
                                                    ...prev,
                                                    phone: phone.target.value
                                                }));
                                            }}
                                            autoComplete="tel"
                                            required
                                        />
                                    </div>

                                    <div className={styles.divider} />

                                    <TextInput
                                        className={styles.field}
                                        label="Poste occupé"
                                        name="job_title"
                                        value={formData.job_title}
                                        onChange={handleChange}
                                    />
                                    <Dropdown
                                        className={styles.field}
                                        label="Niveau du diplôme"
                                        name="highest_degree_level"
                                        value={DEGREE_LEVEL_OPTIONS.find(option => option.value === formData.highest_degree_level) || formData.highest_degree_level}
                                        options={DEGREE_LEVEL_OPTIONS}
                                        setValue={(value) => setFormData(prev => ({
                                            ...prev,
                                            highest_degree_level: value.value || value
                                        }))}
                                    />

                                    <TextInput
                                        className={styles.field}
                                        label="Intitulé du diplôme obtenu"
                                        name="highest_degree_title"
                                        value={formData.highest_degree_title}
                                        onChange={handleChange}
                                    />

                                    <div className={styles.field} style={{ display: 'flex', alignItems: 'center', marginTop: '20px' }}>
                                        <span style={{ marginRight: '10px' }}>Le maître d'apprentissage est-il apte à accueillir un élève en apprentissage ? :</span>
                                        <Switch
                                            name="verified"
                                            value={formData.verified}
                                            onChange={() => handleSwitchChange('verified', !formData.verified)}
                                        />
                                    </div>


                                    <div className={styles.divider} />

                                    <div className={styles.actions} style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                                        <button type="button" className="button-stroke" onClick={onClose}>Annuler</button>
                                        <button type="submit" className="button">Enregistrer</button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </Card>
                </div>
            </div>
        ),
        document.body
    );
};

export default ModalTutor;